import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, Edit, Trash2, Check, X, Clock, User, Shield } from 'lucide-react';
import api from '../services/api';

function ExtraNotesManager({ reportId, user, onNotesUpdate }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [newNoteText, setNewNoteText] = useState('');
  const [editNoteText, setEditNoteText] = useState('');

  const formatTime = timestamp => {
    return new Date(timestamp).toLocaleString();
  };

  const loadNotes = async () => {
    if (!reportId || reportId <= 0) {
      setNotes([]);
      setLoading(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    try {
      let response;
      if (user) {
        // Authenticated users get all notes they have access to
        response = await api.getExtraNotes(reportId);
      } else {
        // Anonymous users only get approved notes
        response = await api.getPublicExtraNotes(reportId);
      }

      // Ensure we always have an array, even if the response is malformed
      const extraNotes = Array.isArray(response?.extraNotes) ? response.extraNotes : [];
      setNotes(extraNotes);
    } catch (err) {
      console.error('Error loading extra notes:', err);
      // Don't show error if it's just that there are no notes or report not found
      if (err.response && (err.response.status === 404 || err.response.status === 403)) {
        setNotes([]);
        setError('');
      } else {
        setError('Failed to load extra notes');
        setNotes([]); // Clear notes on error
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async e => {
    e.preventDefault();
    if (!newNoteText.trim()) return;

    setLoading(true);
    setError('');
    try {
      const noteData = {
        reportId: parseInt(reportId),
        noteText: newNoteText.trim(),
      };

      // For anonymous users, we need to handle the note creation differently
      if (!user) {
        try {
          await api.createAnonymousExtraNote(noteData);
          setNewNoteText('');
          setShowAddForm(false);
          setSuccess(
            'Note submitted successfully! It will be reviewed by administrators before being published.'
          );
          setTimeout(() => setSuccess(''), 5000);
        } catch (err) {
          console.error('Error creating anonymous extra note:', err);
          setError('Failed to submit note. Please try again.');
        }
        return;
      }

      // For authenticated users, proceed normally
      await api.createExtraNote(noteData);
      setNewNoteText('');
      setShowAddForm(false);
      await loadNotes();
      if (onNotesUpdate) onNotesUpdate();
    } catch (err) {
      console.error('Error creating extra note:', err);
      setError('Failed to create extra note');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (reportId && reportId > 0) {
      loadNotes();
    } else {
      setNotes([]);
      setLoading(false);
      setError('');
    }
  }, [reportId, user]);

  // Don't render anything if there's no valid reportId
  if (!reportId || reportId <= 0) {
    return null;
  }

  // For anonymous users, show a simplified interface that allows adding notes
  if (!user) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MessageSquare className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Extra Notes</h3>
            <span className="text-sm text-gray-500">({notes.length})</span>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Add Note</span>
            <span className="text-xs bg-yellow-500 text-white px-1 py-0.5 rounded ml-1">
              Pending Approval
            </span>
          </button>
        </div>

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {showAddForm && (
          <form onSubmit={handleAddNote} className="space-y-3 p-4 bg-gray-50 rounded-lg border">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-blue-600 text-sm">ℹ️</span>
                </div>
                <div className="ml-2">
                  <p className="text-sm text-blue-800">
                    <strong>Anonymous Note:</strong> Your note will be reviewed by administrators
                    before being published. No personal information will be visible to the public.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="newNote" className="block text-sm font-medium text-gray-700 mb-1">
                Add Note
              </label>
              <textarea
                id="newNote"
                value={newNoteText}
                onChange={e => setNewNoteText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="3"
                placeholder="Enter your note here... (will be reviewed by administrators)"
                required
              />
            </div>
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {loading ? 'Adding...' : 'Add Note'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {loading && notes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="mt-2">Loading notes...</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No notes yet. Be the first to add one!</p>
            <p className="text-xs mt-1">
              Notes from anonymous users require administrator approval.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map(note => (
              <div
                key={note.id}
                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-900">
                      {note.userEmail || 'Anonymous User'}
                    </span>
                    <span className="text-xs text-gray-500">{formatTime(note.createdAt)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="flex items-center space-x-1">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span className="text-xs text-green-600 font-medium">Approved</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-gray-800 whitespace-pre-wrap">{note.noteText}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const handleEditNote = async e => {
    e.preventDefault();
    if (!editNoteText.trim()) return;

    setLoading(true);
    setError('');
    try {
      await api.updateExtraNote(editingNote.id, {
        noteText: editNoteText.trim(),
      });
      setEditNoteText('');
      setEditingNote(null);
      await loadNotes();
      if (onNotesUpdate) onNotesUpdate();
    } catch (err) {
      console.error('Error updating extra note:', err);
      setError('Failed to update extra note');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async noteId => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    setLoading(true);
    setError('');
    try {
      await api.deleteExtraNote(noteId);
      await loadNotes();
      if (onNotesUpdate) onNotesUpdate();
    } catch (err) {
      console.error('Error deleting extra note:', err);
      setError('Failed to delete extra note');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveNote = async noteId => {
    setLoading(true);
    setError('');
    try {
      await api.approveExtraNote(noteId);
      await loadNotes();
      if (onNotesUpdate) onNotesUpdate();
    } catch (err) {
      console.error('Error approving extra note:', err);
      setError('Failed to approve extra note');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectNote = async noteId => {
    if (!window.confirm('Are you sure you want to reject and delete this note?')) return;

    setLoading(true);
    setError('');
    try {
      await api.rejectExtraNote(noteId);
      await loadNotes();
      if (onNotesUpdate) onNotesUpdate();
    } catch (err) {
      console.error('Error rejecting extra note:', err);
      setError('Failed to reject extra note');
    } finally {
      setLoading(false);
    }
  };

  const handleUnpublishNote = async noteId => {
    if (
      !window.confirm(
        'Are you sure you want to unpublish this note? It will return to pending status.'
      )
    )
      return;

    setLoading(true);
    setError('');
    try {
      await api.unpublishExtraNote(noteId);
      await loadNotes();
      if (onNotesUpdate) onNotesUpdate();
    } catch (err) {
      console.error('Error unpublishing extra note:', err);
      setError('Failed to unpublish extra note');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = note => {
    setEditingNote(note);
    setEditNoteText(note.noteText);
  };

  const cancelEditing = () => {
    setEditingNote(null);
    setEditNoteText('');
  };

  const canEditNote = note => {
    // Creator can edit before approval, admin can edit any note
    if (!user) return false;
    return (note.userEmail === user.email && !note.isApproved) || user.isAdmin;
  };

  const canDeleteNote = note => {
    // Creator can delete before approval, admin can delete any note
    if (!user) return false;
    return (note.userEmail === user.email && !note.isApproved) || user.isAdmin;
  };

  const canModerateNote = note => {
    if (!user) return false;
    return user.isAdmin && !note.isApproved;
  };

  const canUnpublishNote = note => {
    if (!user) return false;
    return user.isAdmin && note.isApproved;
  };

  // Render the component
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <MessageSquare className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Extra Notes</h3>
          <span className="text-sm text-gray-500">({notes.length})</span>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          <span>Add Note</span>
          {user && user.isAdmin && (
            <span className="text-xs bg-green-500 text-white px-1 py-0.5 rounded ml-1">
              Auto-Approved
            </span>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleAddNote} className="space-y-3 p-4 bg-gray-50 rounded-lg border">
          <div>
            <label htmlFor="newNote" className="block text-sm font-medium text-gray-700 mb-1">
              Add Note
            </label>
            <textarea
              id="newNote"
              value={newNoteText}
              onChange={e => setNewNoteText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              placeholder="Enter your note here..."
              required
            />
          </div>
          <div className="flex space-x-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {loading ? 'Adding...' : 'Add Note'}
            </button>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && notes.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2">Loading notes...</p>
        </div>
      ) : notes.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No extra notes yet. Be the first to add one!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map(note => (
            <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-900">{note.userEmail}</span>
                  <span className="text-xs text-gray-500">{formatTime(note.createdAt)}</span>
                </div>
                <div className="flex items-center space-x-1">
                  {note.isApproved ? (
                    <div className="flex items-center space-x-1">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span className="text-xs text-green-600 font-medium">
                        {note.userEmail === user?.email && user?.isAdmin
                          ? 'Admin Note'
                          : 'Approved'}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4 text-yellow-600" />
                      <span className="text-xs text-yellow-600 font-medium">Pending Approval</span>
                    </div>
                  )}
                  {user && user.isAdmin && note.userEmail !== user.email && (
                    <div className="flex items-center space-x-1">
                      <span className="text-xs text-blue-600 font-medium bg-blue-100 px-1 py-0.5 rounded">
                        Admin Access
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {editingNote?.id === note.id ? (
                <form onSubmit={handleEditNote} className="space-y-2">
                  {user && user.isAdmin && editingNote.userEmail !== user.email && (
                    <div className="p-2 bg-blue-50 border border-blue-200 rounded text-blue-800 text-xs">
                      <strong>Admin Edit:</strong> You are editing a note created by{' '}
                      {editingNote.userEmail}
                    </div>
                  )}
                  <textarea
                    value={editNoteText}
                    onChange={e => setEditNoteText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    required
                  />
                  <div className="flex space-x-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="bg-gray-300 text-gray-700 px-2 py-1 rounded text-xs hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-3">
                  <p className="text-gray-800 whitespace-pre-wrap">{note.noteText}</p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      {canEditNote(note) && (
                        <button
                          onClick={() => startEditing(note)}
                          className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                          title={
                            user && user.isAdmin && note.userEmail !== user.email
                              ? 'Edit note (Admin)'
                              : 'Edit note'
                          }
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {canDeleteNote(note) && (
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                          title={
                            user && user.isAdmin && note.userEmail !== user.email
                              ? 'Delete note (Admin)'
                              : 'Delete note'
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {user && user.isAdmin && (
                      <div className="flex items-center space-x-1">
                        {canModerateNote(note) && (
                          <button
                            onClick={() => handleApproveNote(note.id)}
                            className="flex items-center space-x-1 bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                            title="Approve note"
                          >
                            <Check className="w-3 h-3" />
                            <span>Approve</span>
                          </button>
                        )}
                        {canModerateNote(note) && (
                          <button
                            onClick={() => handleRejectNote(note.id)}
                            className="flex items-center space-x-1 bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                            title="Reject and delete note"
                          >
                            <X className="w-3 h-3" />
                            <span>Reject</span>
                          </button>
                        )}
                        {canUnpublishNote(note) && (
                          <button
                            onClick={() => handleUnpublishNote(note.id)}
                            className="flex items-center space-x-1 bg-yellow-600 text-white px-2 py-1 rounded text-xs hover:bg-yellow-700"
                            title="Unpublish note (return to pending)"
                          >
                            <Clock className="w-3 h-3" />
                            <span>Unpublish</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ExtraNotesManager;
